<template>
  <el-dialog
    v-model="visible"
    width="min(980px, 94vw)"
    :close-on-click-modal="false"
    :close-on-press-escape="!busy"
    :show-close="!busy"
    class="achievement-image-batch-dialog"
    title="批量上传成就徽章"
  >
    <el-alert type="info" :closable="false" show-icon class="naming-alert">
      <template #title>ZIP 内图片按“成就名称 + 状态后缀”自动匹配</template>
      <div class="naming-rules">
        <code>Dusk to Dawn_off.png</code>
        <span>→ Dusk to Dawn 锁定图</span>
        <code>Dusk to Dawn_on.png</code>
        <span>→ Dusk to Dawn 点亮图</span>
      </div>
      <div class="rule-help">后缀不区分大小写；成就名称中的空格、下划线和短横线会自动兼容。ZIP 可以只包含本次要更新的成就；每张源图会由 CMS 自动生成详情 WebP 和 320×320 缩略图。</div>
      <div class="rule-help">存在同名成就时使用“成就名称__achievement_id_on/off.png”，例如 Best Friends__category_best_friends_off.png。</div>
    </el-alert>

    <el-upload
      drag
      accept=".zip,application/zip,application/x-zip-compressed"
      :auto-upload="false"
      :show-file-list="false"
      :disabled="busy || reading"
      :on-change="inspectZip"
    >
      <div class="zip-placeholder">
        <span class="zip-icon">＋</span>
        <strong>{{ selectedFileName || '点击或拖入成就徽章 ZIP' }}</strong>
        <small>{{ reading ? '正在检查文件名与成就配置…' : '上传前只做预检，不会立即保存或发布' }}</small>
      </div>
    </el-upload>

    <template v-if="selectedFileName && !reading">
      <div class="summary-grid">
        <div class="summary-item">
          <span>可匹配成就</span>
          <strong>{{ matchRows.length }}</strong>
        </div>
        <div class="summary-item">
          <span>可上传图片</span>
          <strong>{{ matchedImageCount }}</strong>
        </div>
        <div class="summary-item" :class="{warning: warningCount}">
          <span>需检查文件</span>
          <strong>{{ warningCount }}</strong>
        </div>
        <div class="summary-item" :class="{danger: blockingIssues.length}">
          <span>阻断问题</span>
          <strong>{{ blockingIssues.length }}</strong>
        </div>
      </div>

      <el-alert
        v-if="blockingIssues.length"
        type="error"
        :closable="false"
        show-icon
        title="存在重复或歧义文件，请修正 ZIP 后重新选择"
        class="result-alert"
      />
      <el-alert
        v-else-if="warningCount"
        type="warning"
        :closable="false"
        show-icon
        title="未识别文件不会上传；请确认下方匹配结果后继续"
        class="result-alert"
      />

      <el-table v-if="matchRows.length" :data="matchRows" border size="small" max-height="330">
        <el-table-column prop="achievementName" label="匹配到的成就" min-width="170" />
        <el-table-column label="锁定图 · off" min-width="210">
          <template #default="{row}">
            <span v-if="row.lockedFile" class="file-name">{{ row.lockedFile.name }}</span>
            <el-tag v-else-if="row.hasLocked" type="success" size="small">沿用已配置图片</el-tag>
            <el-tag v-else type="warning" size="small">本次未提供</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="点亮图 · on" min-width="210">
          <template #default="{row}">
            <span v-if="row.unlockedFile" class="file-name">{{ row.unlockedFile.name }}</span>
            <el-tag v-else-if="row.hasUnlocked" type="success" size="small">沿用已配置图片</el-tag>
            <el-tag v-else type="warning" size="small">本次未提供</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="写入后状态" width="120">
          <template #default="{row}">
            <el-tag :type="willBeComplete(row) ? 'success' : 'warning'" size="small">
              {{ willBeComplete(row) ? '两态齐全' : '仍缺图片' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-collapse v-if="issues.length" class="issues-collapse">
        <el-collapse-item :title="`查看 ${issues.length} 个未处理文件/问题`" name="issues">
          <ul class="issues-list">
            <li v-for="(issue, index) in issues" :key="`${issue.fileName}-${index}`">
              <strong>{{ issue.fileName }}</strong>：{{ issue.message }}
            </li>
          </ul>
        </el-collapse-item>
      </el-collapse>
    </template>

    <el-progress
      v-if="busy && progress.total"
      :percentage="progressPercent"
      :status="progress.completed === progress.total ? 'success' : undefined"
      class="upload-progress"
    />

    <template #footer>
      <div class="dialog-footer">
        <span class="footer-help">批量上传只更新本页资源；仍需统一发布图片资源。</span>
        <div>
          <el-button :disabled="busy" @click="visible = false">取消</el-button>
          <el-button
            type="primary"
            :loading="busy"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ busy ? `正在上传 ${progress.completed}/${progress.total}` : `上传并写入 ${matchedImageCount} 张图片` }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import JSZip from 'jszip';
import {ElMessage} from 'element-plus';
import {
  achievementImageFileName,
  createAchievementImageLookup,
  matchAchievementImageFile,
} from '../libs/achievementImageMatcher.mjs';

function imageMimeType(extension) {
  if (extension === 'png') return 'image/png';
  if (extension === 'webp') return 'image/webp';
  return 'image/jpeg';
}

export default {
  name: 'AchievementImageBatchUploader',
  props: {
    modelValue: Boolean,
    achievements: {type: Array, default: () => []},
    busy: Boolean,
    progress: {type: Object, default: () => ({completed: 0, total: 0})},
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      reading: false,
      selectedFileName: '',
      matchRows: [],
      issues: [],
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    blockingIssues() {
      return this.issues.filter(issue => issue.blocking);
    },
    warningCount() {
      return this.issues.filter(issue => !issue.blocking).length;
    },
    matchedImageCount() {
      return this.matchRows.reduce((total, row) => (
        total + Number(Boolean(row.lockedFile)) + Number(Boolean(row.unlockedFile))
      ), 0);
    },
    canSubmit() {
      return !this.busy
        && !this.reading
        && this.matchedImageCount > 0
        && this.blockingIssues.length === 0;
    },
    progressPercent() {
      if (!this.progress.total) return 0;
      return Math.round((this.progress.completed / this.progress.total) * 100);
    },
  },
  watch: {
    modelValue(value) {
      if (value) this.reset();
    },
  },
  methods: {
    reset() {
      this.reading = false;
      this.selectedFileName = '';
      this.matchRows = [];
      this.issues = [];
    },
    willBeComplete(row) {
      return Boolean((row.lockedFile || row.hasLocked) && (row.unlockedFile || row.hasUnlocked));
    },
    async inspectZip(uploadFile) {
      const file = uploadFile?.raw;
      if (!file) return;
      this.reset();
      this.selectedFileName = file.name || '未命名.zip';
      if (!String(file.name || '').toLowerCase().endsWith('.zip')) {
        ElMessage.error('请选择 ZIP 压缩包');
        return;
      }
      if (!file.size || file.size > 500 * 1024 * 1024) {
        ElMessage.error(file.size ? 'ZIP 不能超过 500MB' : '不能上传空 ZIP');
        return;
      }
      this.reading = true;
      try {
        const lookup = createAchievementImageLookup(this.achievements);
        const zip = await JSZip.loadAsync(file);
        const rowMap = new Map();
        const entries = Object.values(zip.files).filter(entry => (
          !entry.dir
          && !entry.name.startsWith('__MACOSX/')
          && !achievementImageFileName(entry.name).startsWith('.')
        ));
        for (const entry of entries) {
          const parsed = matchAchievementImageFile(entry.name, lookup);
          const originalName = achievementImageFileName(entry.name);
          if (parsed.status === 'ignored') {
            this.issues.push({fileName: originalName, message: '不是支持的图片格式，已忽略', blocking: false});
            continue;
          }
          if (parsed.status === 'invalid') {
            this.issues.push({
              fileName: originalName,
              message: '文件名应为“成就名称_on/off.扩展名”',
              blocking: false,
            });
            continue;
          }
          if (parsed.status === 'unmatched') {
            this.issues.push({fileName: originalName, message: `找不到名为“${parsed.achievementName}”的成就`, blocking: false});
            continue;
          }
          if (parsed.status === 'ambiguous') {
            this.issues.push({fileName: originalName, message: '标准化后对应多个同名成就，无法安全匹配', blocking: true});
            continue;
          }
          const achievement = parsed.achievement;
          const rowKey = achievement.achievement_id;
          const row = rowMap.get(rowKey) || {
            achievementId: achievement.achievement_id,
            achievementName: achievement.name,
            hasLocked: Boolean(achievement.display_images?.locked),
            hasUnlocked: Boolean(achievement.display_images?.unlocked),
            lockedFile: null,
            unlockedFile: null,
          };
          if (row[`${parsed.state}File`]) {
            this.issues.push({
              fileName: originalName,
              message: `${achievement.name} 的 ${parsed.state === 'locked' ? 'off' : 'on'} 文件重复`,
              blocking: true,
            });
            continue;
          }
          const blob = await entry.async('blob');
          if (blob.size > 10 * 1024 * 1024) {
            this.issues.push({fileName: originalName, message: '单张图片超过 10MB', blocking: true});
            continue;
          }
          row[`${parsed.state}File`] = new File(
            [blob],
            originalName,
            {type: imageMimeType(parsed.extension)},
          );
          rowMap.set(rowKey, row);
        }
        this.matchRows = Array.from(rowMap.values()).sort((left, right) => (
          left.achievementName.localeCompare(right.achievementName)
        ));
        if (!this.matchedImageCount) {
          ElMessage.warning('ZIP 中没有找到可匹配的成就徽章图片');
        }
      } catch (error) {
        this.matchRows = [];
        this.issues = [];
        ElMessage.error(`ZIP 读取失败：${error?.message || '文件可能已损坏'}`);
      } finally {
        this.reading = false;
      }
    },
    submit() {
      if (!this.canSubmit) return;
      this.$emit('submit', this.matchRows.map(row => ({
        achievementId: row.achievementId,
        achievementName: row.achievementName,
        lockedFile: row.lockedFile,
        unlockedFile: row.unlockedFile,
      })));
    },
  },
};
</script>

<style scoped>
.naming-alert,
.result-alert,
.summary-grid,
.issues-collapse,
.upload-progress {
  margin-bottom: 14px;
}

.naming-rules {
  align-items: center;
  display: grid;
  gap: 6px 12px;
  grid-template-columns: minmax(260px, max-content) 1fr;
  margin-top: 8px;
}

.naming-rules code {
  background: var(--el-fill-color);
  border-radius: 4px;
  color: var(--el-color-primary);
  padding: 3px 7px;
}

.rule-help,
.footer-help {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-top: 8px;
}

.zip-placeholder {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 0;
}

.zip-placeholder small {
  color: var(--el-text-color-secondary);
}

.zip-icon {
  color: var(--el-color-primary);
  font-size: 32px;
  font-weight: 300;
  line-height: 32px;
}

.summary-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 14px;
}

.summary-item {
  background: var(--el-fill-color-light);
  border-radius: 7px;
  padding: 10px 12px;
}

.summary-item span {
  color: var(--el-text-color-secondary);
  display: block;
  font-size: 12px;
}

.summary-item strong {
  display: block;
  font-size: 22px;
  margin-top: 3px;
}

.summary-item.warning strong {
  color: var(--el-color-warning);
}

.summary-item.danger strong {
  color: var(--el-color-danger);
}

.file-name {
  overflow-wrap: anywhere;
}

.issues-list {
  line-height: 1.8;
  margin: 0;
  padding-left: 22px;
}

.dialog-footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

:deep(.achievement-image-batch-dialog) {
  margin-top: 5vh;
}

:deep(.achievement-image-batch-dialog .el-dialog__body) {
  max-height: calc(100vh - 230px);
  overflow-x: hidden;
  overflow-y: auto;
}

@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .dialog-footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
